import { Launch } from '@mui/icons-material';
import { Autocomplete, Box, TextField } from '@mui/material';
import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { DisableContext } from '../../../contexts/DisableContextProvider';
import { useDebouncedValue } from '../../../hooks/useDebounce';
import { ROUTE_PATHS } from '../../../routes/Routes';
import { useGetAllAccounts } from '../../../services/react-query/accounts';
import { Account } from '../../../types/Account';
import { Loading } from '../../../uikit';
import { ErrorObject } from '../../../utility/inputUtils';

interface IAccountSearchAutocompleteProps<Multiple extends boolean | undefined = false> {
  value: Multiple extends true ? Account[] : Account | null;
  onChange: (account: Multiple extends true ? Account[] : Account | null) => void;
  isMultiple?: Multiple;
  accountsOverride?: Account[];
  error?: ErrorObject;
}

export default function AccountSearchAutocomplete<Multiple extends boolean | undefined = false>({
  value,
  accountsOverride,
  error,
  onChange,
  isMultiple,
}: IAccountSearchAutocompleteProps<Multiple>) {
  const [accountSearchInput, setAccountSearchInput] = useState('');
  const debouncedAccountSearchInput = useDebouncedValue(accountSearchInput, 300);
  const { disabled } = useContext(DisableContext);

  const { data: accountsData, isFetching } = useGetAllAccounts({
    params: { search: debouncedAccountSearchInput },
    enabled: !!debouncedAccountSearchInput,
  });

  const accounts = accountsOverride || accountsData?.data || [];

  return (
    <Box sx={{ display: 'inline-flex', gap: 1, width: '100%' }}>
      <Autocomplete<Account, Multiple, false, false>
        fullWidth
        multiple={isMultiple}
        loading={isFetching}
        loadingText={<Loading size={22} />}
        options={accounts}
        noOptionsText={debouncedAccountSearchInput ? 'No accounts found' : 'Search for accounts'}
        disabled={disabled}
        onChange={(_, account) => onChange(account)}
        getOptionLabel={(account) => account.name}
        value={value}
        renderInput={(params) => <TextField {...params} {...error} label="Account" onChange={(e) => setAccountSearchInput(e.target.value)} />}
      />
      {!isMultiple && value && (
        <Link to={`/${ROUTE_PATHS.ACCOUNTS}/${(value as Account).uuid}`} style={{ alignContent: 'center' }}>
          <Launch sx={{ color: 'black' }} />
        </Link>
      )}
    </Box>
  );
}
